import type { OnInit } from '@angular/core';
import type { Observable } from 'rxjs';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';
import type { AdvancedRadiusSettings } from '@components/sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormField } from '@angular/forms/signals';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsModalService } from 'ngx-bootstrap/modal';

import { extendWebFileDataPromise } from '@common/helpers';
import { setChatAt } from '@common/helpers/string';
import { buildFileSrcSets } from '@components/sp-file-manager/src/helpers';
import { IMAGE_BORDER_RADIUS_BOUNDS } from '@sites/entity-core';
import {
    EConditionType,
    EHorizontalPosition,
    ELineStyle,
    EOverlayType,
    ESize,
    EVerticalPosition,
    WidgetsDataTypes,
} from '@common/enums';
import { backgroundPositionOptions } from '@common/constants/background-position-options.const';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type { AdvancedBorder, ExtendedPositionLegacy, ImageEntity, ShadowSettingsLegacy } from '@sites/entity-core';
import type { ImageDefaultForm, ImageForm, ImageZeroForm } from '@sites/entity-signal-form';

import { environment } from '@web-env/environment';
import type { WebLegacyWidgetEntity } from '@web/types';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ImageSettingsComponent } from '@web/widgets/image/components/image-control/image-settings/image-settings.component';
import { EResizeType } from '@web/widgets/image/type/image-widget.type';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { ImageEditorService } from '@web/shared/modals/image-editor-modal/image-editor.service';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpShadowSettingsSignalLegacyComponent } from '@components/sp-shadow-settings-signal-legacy';
import { SpAdvancedRadiusSettingsModule } from '@components/sp-advanced-radius-settings';
import { SpAdvancedBorderSettingsSignalLegacyComponent } from '@components/sp-advanced-border-settings-signal-legacy';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpAnimationSettingsSignalLegacyComponent } from '@components/sp-animation-settings-signal-legacy';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import { ImageSizeLimitModalComponent } from '@web/shared/modals/image-size-limit-modal/image-size-limit-modal.component';

type ImageFile = NonNullable<ImageEntity['data']['image']>;

/**
 * Overlay/layout defaults applied to a freshly-picked image — mirrors the
 * `extendedFile` block the old `imageWidgetModel.dataAdapter` merged under the
 * pick (the signal-form widget has no data adapter, so the control seeds these
 * at pick time).
 */
const IMAGE_OVERLAY_DEFAULTS = {
    isImageEffect: false,
    condition: EConditionType.ALWAYS,
    overlay: EOverlayType.TEXT,
    textSize: ESize.M,
    fontSize: 14,
    textStyle: [] as string[],
    textColor: '#ffffff',
    textBackground: 'rgba(0,0,0,0.5)',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,
    textPosition: EVerticalPosition.B,
    textJustify: EHorizontalPosition.L,
    maskShape: null,
    overlayImage: null,
};

/**
 * Editor control panel for the `image` widget — fully signal-based. The form
 * is created in `ngOnInit` via `WebLegacyWidgetSignalFormService.getOrCreateScoped`,
 * shared (ref-counted) with the view component for the same widget. Default
 * and zero placements share this panel; the template branches on the form's
 * `type` discriminator.
 *
 * The template reads the form's FieldTree accessors directly (`form.x().value()`
 * via `@let`, `form.setX(...)` on events, `[formField]` for native controls), so
 * this class holds no value-mirror getters — only the form lifecycle, file-pick
 * orchestration, and a few composed DTOs / settings the child components need.
 */
@Component({
    selector: 'web-image-control',
    templateUrl: './image-control.component.html',
    styleUrls: ['./image-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        FormField,
        SpFileManagerComponentModule,
        TooltipModule,
        ImageSettingsComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpShadowSettingsSignalLegacyComponent,
        SpAdvancedRadiusSettingsModule,
        SpAdvancedBorderSettingsSignalLegacyComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpAnimationSettingsSignalLegacyComponent,
    ],
})
export class ImageControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<ImageEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly imageEditorService: ImageEditorService = inject(ImageEditorService);
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public form!: ImageForm;

    public readonly isDeviceTablet$ = this.layoutFacade.isDeviceTablet$;
    public readonly isDeviceMobile$ = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceDesktop$ = this.layoutFacade.isDeviceDesktop$;

    private readonly isDeviceTabletSig = toSignal(this.isDeviceTablet$, { initialValue: false });
    private readonly isDeviceMobileSig = toSignal(this.isDeviceMobile$, { initialValue: false });

    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EResizeType = EResizeType;
    public readonly fmSourceType = FmSourceType;

    public readonly imageBaseUrl = `${environment.fileManagerCdnUrl}`;
    public readonly borderRadiusBounds = IMAGE_BORDER_RADIUS_BOUNDS;

    public readonly editableImage = signal<SelectedFileExtended | null>(null);
    public readonly buttonDisabled = signal<boolean>(false);
    public readonly columnWidth = signal<number | undefined>(undefined);

    public readonly userId$: Observable<number> = this.userFacade.userId$;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<ImageForm>(this.widget().id, this.destroyRef);
    }

    // --- Form-derived helpers (composed / cast — no 1:1 form accessor) ---
    /** True when the widget lives inside a zero block. */
    public readonly isZero = computed<boolean>(() => this.form.value().type === 'zero');

    /**
     * The form narrowed to the default variant — see the button control for the
     * casting rationale. Only valid (and only read) on non-zero placements; the
     * dispatcher built whichever variant matches the entity's `type`.
     */
    public get defaultForm(): ImageDefaultForm {
        return this.form as ImageDefaultForm;
    }

    /**
     * The form narrowed to the zero variant — exposes the extended `position`
     * grid and the zero-only `imagePosition` FieldTree node (bound to the native
     * `<select>` via `[formField]`). Only read inside the zero-widget branch.
     */
    public get zeroForm(): ImageZeroForm {
        return this.form as ImageZeroForm;
    }

    /**
     * The zero variant's extended position value — used by the pick/resize math
     * and the extended-position settings panel.
     */
    public zeroPosition(): ExtendedPositionLegacy | null {
        if (!this.form) return null;
        return this.zeroForm.position().value();
    }

    public readonly isAspectRatioChanged = computed<boolean>(() => {
        const image = this.form.image().value();
        if (!image?.imageWidth || !image?.imageHeight) return false;

        const { currentWidth, currentHeight } = this.getCurrentDevicePositionSize();
        if (!currentWidth || !currentHeight) return false;

        const resizeType = this.form.resizeType().value();
        const { width, height } =
            !resizeType || resizeType === EResizeType.scale
                ? this.clampToMaxSize(image.imageWidth, image.imageHeight)
                : { width: image.imageWidth, height: image.imageHeight };

        const originalRatio = width / height;
        const currentRatio = currentWidth / currentHeight;
        // 1% tolerance for Math.round pixel rounding in size calculations
        return Math.abs(originalRatio - currentRatio) / originalRatio > 0.01;
    });

    private getCurrentDevicePositionSize(): { currentWidth: number; currentHeight: number } {
        const position = this.zeroPosition();
        if (!position) return { currentWidth: 0, currentHeight: 0 };

        if (this.isDeviceTabletSig()) {
            return { currentWidth: position.tabletWidth, currentHeight: position.tabletHeight };
        }
        if (this.isDeviceMobileSig()) {
            return { currentWidth: position.mobileWidth, currentHeight: position.mobileHeight };
        }
        return { currentWidth: position.width, currentHeight: position.height };
    }

    /** Radius DTO for `sp-advanced-radius-settings`, composed from the styles group. */
    public readonly radiusSettings = computed<AdvancedRadiusSettings>(() => ({
        borderTopLeftRadius: this.form.borderTopLeftRadius().value() ?? 0,
        borderTopRightRadius: this.form.borderTopRightRadius().value() ?? 0,
        borderBottomRightRadius: this.form.borderBottomRightRadius().value() ?? 0,
        borderBottomLeftRadius: this.form.borderBottomLeftRadius().value() ?? 0,
    }));

    public shadowSettings(): ShadowSettingsLegacy | null {
        return this.form.shadowSettings?.().value() ?? null;
    }

    /** Border DTO for `sp-advanced-border-settings-signal-legacy` — falls back to the simple border fields. */
    public readonly advancedBorderSettings = computed<AdvancedBorder>(() => {
        const stored = this.form.advancedBorderSettings().value();
        if (stored) {
            return stored;
        }

        const width = this.form.borderWidth().value() ?? 1;
        const style = this.form.borderStyle().value() ?? ELineStyle.SOLID;
        const color = this.form.borderColor().value() ?? '#000000';

        return {
            isActive: Boolean(this.form.isBorder().value()),
            isIndependentBorders: false,
            borderTopWidth: width,
            borderTopColor: color,
            borderTopStyle: style,
            borderRightWidth: width,
            borderRightColor: color,
            borderRightStyle: style,
            borderBottomWidth: width,
            borderBottomColor: color,
            borderBottomStyle: style,
            borderLeftWidth: width,
            borderLeftColor: color,
            borderLeftStyle: style,
        };
    });

    public readonly imageOwnerUserId = computed<number>(() => {
        const urlParts = this.form.image().value()?.url?.split('/');
        return Number(urlParts[1].substring(32));
    });

    // --- File picking ---

    public async setImage(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled.set(true);
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        if (extendedFile.imageHeight >= 8000 || extendedFile.imageWidth >= 8000) {
            this.modalService.show(ImageSizeLimitModalComponent, { initialState: { imageName: extendedFile.name } });
        } else {
            const srcSets = buildFileSrcSets(extendedFile, environment.fileManagerCdnUrl);

            this.form.transact(() => {
                if (this.isZero()) {
                    const position = this.zeroPosition()!;
                    if (!this.form.image().value() && this.form.resizeType().value() === EResizeType.scale) {
                        const { width, height } = this.clampToMaxSize(
                            extendedFile.imageWidth,
                            extendedFile.imageHeight
                        );
                        this.form.setPosition({ ...position, height, width });
                    } else {
                        this.form.setPosition({
                            ...position,
                            height: this.getProportionalHeight(
                                extendedFile.imageWidth,
                                extendedFile.imageHeight,
                                position.width
                            ),
                        });
                    }
                }

                this.form.setMaxWidth(extendedFile.imageWidth);
                this.form.setImage({ ...IMAGE_OVERLAY_DEFAULTS, ...extendedFile, srcSets } as unknown as ImageFile);
            });

            this.siteFacade.resizeImage({
                path: extendedFile.path,
                url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
            });
        }

        this.buttonDisabled.set(false);
        this.changeDetectorRef.detectChanges();
    }

    public async replaceImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled.set(true);
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        if (extendedFile.imageHeight >= 8000 || extendedFile.imageWidth >= 8000) {
            this.modalService.show(ImageSizeLimitModalComponent, { initialState: { imageName: extendedFile.name } });
        } else {
            this.form.transact(() => {
                this.form.setImage(extendedFile as unknown as ImageFile);
                this.form.setMaxWidth(extendedFile.imageWidth);
            });

            this.siteFacade.resizeImage({
                path: extendedFile.path,
                url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
            });
        }

        this.buttonDisabled.set(false);
    }

    private getProportionalHeight(originalWidth: number, originalHeight: number, containerWidth: number): number {
        return (originalHeight / originalWidth) * containerWidth;
    }

    private readonly ZERO_WIDGET_MAX_SIZE = 600;
    private clampToMaxSize(width: number, height: number): { width: number; height: number } {
        if (height <= this.ZERO_WIDGET_MAX_SIZE && width <= this.ZERO_WIDGET_MAX_SIZE) {
            return { width, height };
        }
        const [bigger, smaller] = height > width ? [height, width] : [width, height];
        const ratio = smaller / bigger;
        const calculatedSize = Math.round(this.ZERO_WIDGET_MAX_SIZE * ratio);
        return height > width
            ? { width: calculatedSize, height: this.ZERO_WIDGET_MAX_SIZE }
            : { width: this.ZERO_WIDGET_MAX_SIZE, height: calculatedSize };
    }

    /**
     * Open (or close, when passed `null`) the image-effect settings sub-panel.
     * On open we snapshot the picked file straight from the live form — the
     * signal form mutates immutably, so this reference stays frozen at the
     * pre-edit value and lets {@link cancelImageSettings} restore it exactly.
     */
    public editImage(image: SelectedFileExtended | null): void {
        this.editableImage.set(image ? (this.form.image().value() as unknown as SelectedFileExtended) : null);
    }

    /** Revert the picked file to the snapshot taken when the sub-panel opened, then close it. */
    public cancelImageSettings(): void {
        const snapshot = this.editableImage();
        if (snapshot && this.form.image().value() !== (snapshot as unknown)) {
            this.form.setImage(snapshot as unknown as ImageFile);
        }
        this.editImage(null);
    }

    public openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({ widgetId: this.widget().id, dataType: WidgetsDataTypes.image });
    }

    public removeFile(): void {
        this.form.setImage(null);
    }

    public showEditImageModal(): void {
        this.imageEditorService.showEditImageModal(
            this.form.image().value() as unknown as SelectedFileExtended,
            this.form.value() as any,
            WidgetsDataTypes.image
        );
    }

    public resetProportion(): void {
        const image = this.form.image().value()!;
        const position = this.zeroPosition()!;
        const heightKey = this.isDeviceTabletSig()
            ? 'tabletHeight'
            : this.isDeviceMobileSig()
              ? 'mobileHeight'
              : 'height'; // todo: remove ternary
        const widthKey = this.isDeviceTabletSig() ? 'tabletWidth' : this.isDeviceMobileSig() ? 'mobileWidth' : 'width'; // todo: remove ternary

        const height = this.getProportionalHeight(image.imageWidth, image.imageHeight, position[widthKey]);

        this.form.setPosition({ ...position, [heightKey]: height });
        this.form.updateImage({ resizedHeight: null, resizedWidth: null });
    }

    public refreshImageWidth(): void {
        const image = this.form.image().value();
        const maxWidth = Number(this.form.maxWidth().value());
        if (maxWidth < image?.imageWidth && image?.imageWidth > this.columnWidth()) {
            this.form.setMaxWidth(image.imageWidth);
        } else if (maxWidth < image?.imageWidth) {
            this.form.setMaxWidth(image.imageWidth);
        }
    }

    // --- Border / radius / shadow ---

    public toggleIndependentCorners(): void {
        this.setBorderRadius(this.form.borderTopLeftRadius().value() ?? 0);
        this.form.setIsIndependentCorners(!this.form.isIndependentCorners().value());
    }

    public setAdvancedRadiusSettings(updatedSettings: AdvancedRadiusSettings): void {
        const current = this.radiusSettings();

        this.form.transact(() => {
            if (updatedSettings.borderTopLeftRadius !== current.borderTopLeftRadius) {
                this.form.setBorderTopLeftRadius(updatedSettings.borderTopLeftRadius);
            }
            if (updatedSettings.borderTopRightRadius !== current.borderTopRightRadius) {
                this.form.setBorderTopRightRadius(updatedSettings.borderTopRightRadius);
            }
            if (updatedSettings.borderBottomRightRadius !== current.borderBottomRightRadius) {
                this.form.setBorderBottomRightRadius(updatedSettings.borderBottomRightRadius);
            }
            if (updatedSettings.borderBottomLeftRadius !== current.borderBottomLeftRadius) {
                this.form.setBorderBottomLeftRadius(updatedSettings.borderBottomLeftRadius);
            }
        });
    }

    public setBorderRadius(value: number): void {
        this.form.transact(() => {
            this.form.setBorderTopLeftRadius(value);
            this.form.setBorderTopRightRadius(value);
            this.form.setBorderBottomRightRadius(value);
            this.form.setBorderBottomLeftRadius(value);
        });
    }

    public setAdvancedBorderSettings(updatedSettings: AdvancedBorder): void {
        this.form.setAdvancedBorderSettings({ ...updatedSettings, isActive: Boolean(this.form.isBorder().value()) });
    }

    public setResizeType(value: EResizeType): void {
        this.form.transact(() => {
            this.form.setResizeType(value);

            if (value === EResizeType.scale && this.isZero() && this.isAspectRatioChanged()) {
                this.resetProportion();
            }
        });
    }

    // --- Alignment / visibility ---

    public setSynchronizeStyle(): void {
        this.form.transact(() => {
            const align = this.form.horizontalAlign().value();
            this.form.setMobileHorizontalAlign(align as never);
            this.form.setTabletHorizontalAlign(align as never);
            this.form.setIsSynchronizeStyle(!this.form.isSynchronizeStyle().value());
        });
    }

    public setHorizontalAlign(value: string): void {
        this.form.transact(() => {
            if (this.form.isSynchronizeStyle().value()) {
                this.form.setMobileHorizontalAlign(value as never);
                this.form.setTabletHorizontalAlign(value as never);
            }
            this.form.setHorizontalAlign(value as never);
        });
    }

    public setMobileHorizontalAlign(value: string): void {
        this.form.setMobileHorizontalAlign(value as never);
    }

    public setTabletHorizontalAlign(value: string): void {
        this.form.setTabletHorizontalAlign(value as never);
    }

    protected readonly backgroundPositionOptions = backgroundPositionOptions;
}
